import React from 'react';
import { Row, Col, Card, Typography, Button, Badge, Space } from 'antd';
import { 
  ClockCircleOutlined,
  CheckSquareOutlined,
  BulbOutlined,
  BookOutlined,
  EditOutlined,
  RightOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title, Text } = Typography;
const { Meta } = Card;

const Dashboard = () => {
  const navigate = useNavigate();

  const activeApps = [
    {
      id: 'pomodoro',
      title: 'Pomodoro Timer',
      description: 'Focus sessions with work and break intervals',
      icon: <ClockCircleOutlined style={{ fontSize: '48px', color: '#1E90FF' }} />,
      color: '#1E90FF',
      available: true,
      path: '/pomodoro',
      stats: 'Track your focus sessions'
    },
    {
      id: 'habit-tracker',
      title: 'Habit Tracker',
      description: 'Build and maintain positive daily habits',
      icon: <CheckSquareOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      color: '#52c41a',
      available: true,
      path: '/habits',
      stats: 'Build lasting habits'
    }
  ];

  const comingSoonApps = [
    {
      id: 'goal-setter',
      title: 'Goal Setter',
      description: 'Set, track, and achieve your goals',
      icon: <BulbOutlined style={{ fontSize: '48px', color: '#FFA500' }} />,
      color: '#FFA500',
      available: false,
      comingSoon: true
    },
    {
      id: 'assignment-tracker',
      title: 'Assignment Tracker',
      description: 'Manage academic assignments and deadlines',
      icon: <CalendarOutlined style={{ fontSize: '48px', color: '#722ed1' }} />,
      color: '#722ed1',
      available: false,
      comingSoon: true
    },
    {
      id: 'flashcard-decks',
      title: 'Flashcard Decks',
      description: 'Create and study with digital flashcards',
      icon: <BookOutlined style={{ fontSize: '48px', color: '#eb2f96' }} />,
      color: '#eb2f96',
      available: false,
      comingSoon: true
    },
    {
      id: 'digital-journal',
      title: 'Digital Journal',
      description: 'Reflect and document your journey',
      icon: <EditOutlined style={{ fontSize: '48px', color: '#13c2c2' }} />,
      color: '#13c2c2',
      available: false,
      comingSoon: true
    }
  ];

  const handleAppClick = (app) => {
    if (app.available && app.path) {
      navigate(app.path);
    }
  };

  const AppCard = ({ app }) => (
    <Card
      hoverable={app.available}
      style={{
        height: '100%',
        borderRadius: '12px',
        boxShadow: app.available 
          ? '0 4px 12px rgba(0,0,0,0.1)' 
          : '0 2px 6px rgba(0,0,0,0.05)',
        cursor: app.available ? 'pointer' : 'not-allowed',
        opacity: app.available ? 1 : 0.7,
        border: app.available ? `2px solid ${app.color}20` : '1px solid #d9d9d9',
        transition: 'all 0.3s ease'
      }}
      onClick={() => handleAppClick(app)}
      actions={app.available ? [
        <Button 
          type="primary" 
          icon={<RightOutlined />}
          style={{ 
            backgroundColor: app.color,
            borderColor: app.color
          }}
        >
          Launch
        </Button>
      ] : [
        <Badge.Ribbon text="Coming Soon" color="#FFA500">
          <Button disabled style={{ width: '100%' }}>
            Not Available
          </Button>
        </Badge.Ribbon>
      ]}
    >
      <div style={{ textAlign: 'center', padding: '20px 0' }}>
        <div style={{ marginBottom: '16px' }}>
          {app.icon}
        </div>
        <Meta
          title={
            <Title level={4} style={{ 
              margin: 0, 
              color: app.available ? '#000080' : '#8c8c8c'
            }}>
              {app.title}
            </Title>
          }
          description={
            <div>
              <Text type={app.available ? 'secondary' : 'disabled'} style={{ fontSize: '14px' }}>
                {app.description}
              </Text>
              {app.stats && (
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px', fontStyle: 'italic' }}>
                    {app.stats}
                  </Text>
                </div>
              )}
            </div>
          }
        />
      </div>
    </Card>
  );

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header Section */}
      <div style={{ marginBottom: '32px', textAlign: 'center' }}>
        <Title level={1} style={{ 
          color: '#000080',
          marginBottom: '8px',
          fontSize: '2.5rem'
        }}>
          Welcome to Progress Craft
        </Title>
        <Text style={{ 
          fontSize: '18px', 
          color: '#4B4B4B',
          display: 'block',
          marginBottom: '16px'
        }}>
          Your productivity hub for building better habits and achieving more
        </Text>
        <Text type="secondary" style={{ fontSize: '16px' }}>
          Choose an app below to get started on your productivity journey
        </Text>
      </div>

      {/* Active Apps Section */}
      <div style={{ marginBottom: '48px' }}>
        <Title level={2} style={{ 
          color: '#000080',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ 
            width: '4px', 
            height: '24px', 
            backgroundColor: '#1E90FF',
            marginRight: '12px',
            borderRadius: '2px'
          }}></span>
          Available Apps
        </Title>
        
        <Row gutter={[24, 24]}>
          {activeApps.map((app) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={app.id}>
              <AppCard app={app} />
            </Col>
          ))}
        </Row>
      </div>

      {/* Coming Soon Section */}
      <div>
        <Title level={2} style={{ 
          color: '#000080',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center'
        }}>
          <span style={{ 
            width: '4px', 
            height: '24px', 
            backgroundColor: '#FFA500',
            marginRight: '12px',
            borderRadius: '2px'
          }}></span>
          Coming Soon
        </Title>
        
        <Row gutter={[24, 24]}>
          {comingSoonApps.map((app) => (
            <Col xs={24} sm={12} lg={8} xl={6} key={app.id}>
              <AppCard app={app} />
            </Col>
          ))}
        </Row>
      </div>

      {/* Footer CTA */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '48px',
        padding: '32px',
        backgroundColor: '#f8f9fa',
        borderRadius: '12px',
        border: '1px solid #e9ecef'
      }}>
        <Title level={3} style={{ color: '#000080', marginBottom: '16px' }}>
          Ready to boost your productivity?
        </Title>
        <Text style={{ fontSize: '16px', color: '#4B4B4B', display: 'block', marginBottom: '24px' }}>
          Start with the Pomodoro Timer to build focus, then track your progress with the Habit Tracker
        </Text>
        <Space size="large">
          <Button 
            type="primary" 
            size="large"
            icon={<ClockCircleOutlined />}
            onClick={() => navigate('/pomodoro')}
            style={{ 
              backgroundColor: '#1E90FF',
              borderColor: '#1E90FF',
              height: '44px',
              fontSize: '16px'
            }}
          >
            Start Focus
          </Button>
          <Button 
            size="large"
            icon={<CheckSquareOutlined />}
            onClick={() => navigate('/habits')}
            style={{ 
              height: '44px',
              fontSize: '16px',
              borderColor: '#52c41a',
              color: '#52c41a'
            }}
          >
            Track Habits
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default Dashboard; 